import { SkeletonLoader } from "@via/components/loading/loading.tsx";
import { PageHeading } from "@via/components/page-heading";
import { HorizontalTabs } from "@via/components/tabs";
import ScanButton from "@via/pages/scan/scan-button";
import EventFormUnavailableNote from "@via/pages/projects/metadata/event-form-unavailable-note";
import { useAuth } from "@via/hooks/useAuth";
import LegacyIframe from "@via/pages/projects/legacy-iframe";
import { useGlobal } from "@via/hooks/useGlobal.tsx";
import Collections from "@via/pages/projects/metadata/collections/collections";
import AllCollections from "@via/pages/projects/metadata/configuration/collections/collections";
import Fields from "@via/pages/projects/metadata/configuration/fields/fields";
import DataImport from "@via/pages/projects/metadata/configuration/data-import/data-import";
import ApiConfigs from "@via/pages/projects/metadata/configuration/api-configs/api-configs";
import EventFormsConfig from "@via/pages/projects/metadata/configuration/event-forms/event-forms-config";
import EventsTab from "@via/pages/projects/metadata/events/events-tab";
import SampleExplorer from "@via/pages/sample-trackers/samples/sample-explorer";
import {
  useProjectList,
  useVmetaCanvas,
} from "@via/pages/projects/projects.api.tsx";
import {
  METADATA_CONFIGURATION_TABS,
  METADATA_DATAVIEW_TABS,
} from "@via/utils/constants";
import { classNames } from "@via/utils/helpers";
import { type JSX, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

export default function Metadata({
  showPageHeading = false,
}: {
  showPageHeading?: boolean;
}): JSX.Element {
  const { projectId = "" } = useParams();
  const location = useLocation();
  const { selectedProject } = useGlobal();
  const [vMetaUrl, setVMetaUrl] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);

  const { config } = useAuth();
  const eventsEnabled = !!(showPageHeading && config?.VMETA_EVENTS_ENABLED);

  // The route decides the Data View tab. An events url without the flag shows Collections.
  const tabForPath = (pathname: string) =>
    pathname.includes("/sample-explorer")
      ? METADATA_DATAVIEW_TABS.SAMPLE_EXPLORER
      : eventsEnabled && /\/events(\/|$)/.test(pathname)
        ? METADATA_DATAVIEW_TABS.EVENTS
        : METADATA_DATAVIEW_TABS.COLLECTIONS;

  const [activeTab, setActiveTab] = useState(tabForPath(location.pathname));
  const [isConfigureMetadataActive, setIsConfigureMetadataActive] =
    useState(false);
  const [selectedCollectionName, setSelectedCollectionName] = useState<
    string | null
  >(null);
  const navigate = useNavigate();

  // Sync active tab with route
  useEffect(() => {
    setActiveTab(tabForPath(location.pathname));
  }, [location.pathname, eventsEnabled]);

  // A record link (?record=) always lands on the data view's Collections tab, also when the
  // Configuration view was open and the link arrives without a remount. location.key changes on
  // every navigation, so following the same link twice still switches back.
  const recordInUrl = new URLSearchParams(location.search).get("record");
  useEffect(() => {
    if (!recordInUrl) return;
    setIsConfigureMetadataActive(false);
    setActiveTab(METADATA_DATAVIEW_TABS.COLLECTIONS);
  }, [recordInUrl, location.key]);

  const { data: projectListData, isSuccess: isProjectListSuccess } =
    useProjectList();

  const canvasId = showPageHeading ? projectId : "";

  const { data: canvasData } = useVmetaCanvas(canvasId);

  useEffect(() => {
    if (projectListData && projectId) {
      const slug = "vproject";
      const url = showPageHeading
        ? `/vmeta/${slug}/${projectId}`
        : `/vmeta/${slug}/${selectedProject?.vmetaId}`;
      setVMetaUrl(url);
      setIsLoading(false);
    }

    if (!config.ENABLE_VMETA) {
      navigate(`/projects/${projectId}`);
    }
  }, [
    isProjectListSuccess,
    config,
    projectId,
    selectedProject?.vmetaId,
    showPageHeading,
  ]);

  return (
    <main className="p-4">
      {showPageHeading && canvasData?.data && (
        <>
          <div className="via-page-heading-wrapper flex items-center justify-between gap-4">
            <PageHeading title={canvasData.data?.label} />
            <ScanButton projectId={projectId} />
          </div>
          <EventFormUnavailableNote />
        </>
      )}
      {isLoading || !config.ENABLE_VMETA ? (
        <div className="grid grid-cols-1 gap-6 py-6 sm:grid-cols-2 lg:grid-cols-3">
          <SkeletonLoader count={12} />
        </div>
      ) : showPageHeading || config.USE_VMETA_V2 ? (
        <>
          <span className="isolate inline-flex justify-end w-full">
            <button
              type="button"
              onClick={() => {
                setIsConfigureMetadataActive(false);
                setActiveTab(METADATA_DATAVIEW_TABS.COLLECTIONS);
              }}
              className={classNames(
                "relative inline-flex h-9 items-center rounded-l-md  px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10",
                !isConfigureMetadataActive
                  ? "bg-indigo-600 text-white hover:bg-indigo-500  hover:shadow-md"
                  : "bg-white"
              )}>
              Data View
            </button>
            <button
              type="button"
              onClick={() => {
                setIsConfigureMetadataActive(true);
                setActiveTab(METADATA_CONFIGURATION_TABS.ALL_COLLECTIONS);
              }}
              className={classNames(
                "relative -ml-px inline-flex h-9 items-center rounded-r-md  px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10",
                isConfigureMetadataActive
                  ? "bg-indigo-600 text-white hover:bg-indigo-500  hover:shadow-md"
                  : "bg-white"
              )}>
              Configuration
            </button>
          </span>
          {isConfigureMetadataActive ? (
            <HorizontalTabs
              buttonIdPrefix="metadata"
              activeTab={activeTab}
              onTabChange={setActiveTab}
              options={[
                {
                  name: METADATA_CONFIGURATION_TABS.ALL_COLLECTIONS,
                  children: (
                    <AllCollections
                      projectId={
                        showPageHeading
                          ? projectId
                          : (selectedProject?.vmetaId ?? "")
                      }
                      activeTab={activeTab}
                      onCollectionClick={collection => {
                        setSelectedCollectionName(collection.label);
                        setActiveTab(METADATA_CONFIGURATION_TABS.FIELDS);
                      }}
                    />
                  ),
                },
                {
                  name: METADATA_CONFIGURATION_TABS.FIELDS,
                  children: (
                    <Fields
                      projectId={
                        showPageHeading
                          ? projectId
                          : (selectedProject?.vmetaId ?? "")
                      }
                      activeTab={activeTab}
                      initialSelectedCollectionName={selectedCollectionName}
                      onCollectionSelected={() =>
                        setSelectedCollectionName(null)
                      }
                    />
                  ),
                },
                {
                  name: METADATA_CONFIGURATION_TABS.API_CONFIG,
                  children: (
                    <ApiConfigs
                      projectId={
                        showPageHeading
                          ? projectId
                          : (selectedProject?.vmetaId ?? "")
                      }
                      activeTab={activeTab}
                    />
                  ),
                },
                {
                  name: METADATA_CONFIGURATION_TABS.DATA_IMPORT,
                  children: (
                    <DataImport
                      projectId={
                        showPageHeading
                          ? projectId
                          : (selectedProject?.vmetaId ?? "")
                      }
                      activeTab={activeTab}
                    />
                  ),
                },
                ...(eventsEnabled
                  ? [
                      {
                        name: METADATA_CONFIGURATION_TABS.EVENT_FORMS,
                        children: (
                          <EventFormsConfig
                            canvasId={projectId}
                            isActive={
                              activeTab ===
                              METADATA_CONFIGURATION_TABS.EVENT_FORMS
                            }
                          />
                        ),
                      },
                    ]
                  : []),
              ]}
            />
          ) : (
            <HorizontalTabs
              buttonIdPrefix="metadata"
              activeTab={activeTab}
              onTabChange={tab => {
                setActiveTab(tab);
                if (showPageHeading && projectId) {
                  if (tab === METADATA_DATAVIEW_TABS.SAMPLE_EXPLORER) {
                    navigate(`/sample-trackers/${projectId}/sample-explorer`);
                  } else if (tab === METADATA_DATAVIEW_TABS.COLLECTIONS) {
                    navigate(`/sample-trackers/${projectId}`);
                  } else if (tab === METADATA_DATAVIEW_TABS.EVENTS) {
                    navigate(`/sample-trackers/${projectId}/events`);
                  }
                }
              }}
              options={[
                {
                  name: METADATA_DATAVIEW_TABS.COLLECTIONS,
                  children: (
                    <Collections
                      canvasId={
                        showPageHeading
                          ? projectId
                          : (selectedProject?.vmetaId ?? "")
                      }
                      initialSelectedCollectionName={selectedCollectionName}
                      onCollectionSelected={() => {
                        setSelectedCollectionName(null);
                      }}
                    />
                  ),
                },
                // Only show Sample Explorer tab in sample-trackers context (not in projects)
                ...(showPageHeading
                  ? [
                      {
                        name: METADATA_DATAVIEW_TABS.SAMPLE_EXPLORER,
                        children: <SampleExplorer projectId={projectId} />,
                      },
                    ]
                  : []),
                ...(eventsEnabled
                  ? [
                      {
                        name: METADATA_DATAVIEW_TABS.EVENTS,
                        children: <EventsTab canvasId={projectId} />,
                      },
                    ]
                  : []),
              ]}
            />
          )}
        </>
      ) : (
        <LegacyIframe src={vMetaUrl} prefix="/vmeta" />
      )}
    </main>
  );
}
